import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    get as _get,
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Backdrop from '@mui/material/Backdrop';
import LinearProgress from '@mui/material/LinearProgress';

import DeleteIcon from '@mui/icons-material/Delete';

import Validator, { useValidate, checkValidity } from '../../components/validator';
import ConfirmationDialog from '../../components/confirmationdlg';
import DataContext from '../../data/data-context';

import SensorsService from '../../data/sensors-service';
import PolicyService from '../../data/policy-service';
import Auth from '../../data/auth-service';
import BacktestingService from '../../data/backtesting-service';
import POLICY_NAMES from '../../data/policies';

const useStyles = makeStyles(theme => ({
    fullWidth: {
        width: '100%',
        minHeight: '36px',
    },
    sensorConfigs: {
        width: '100%',
        background: theme.palette.background.default,
        padding: theme.spacing(3),
        height: '100%',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    backdrop: {
        position: 'fixed',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        zIndex: 10000,
    },
}));

const AddSensor = () => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const params = useParams();
    const [policies, setPolicies] = useState([]);
    const [updateSensor, setUpdateSensor] = useState(null);
    const [newSensor, setNewSensor] = useState({
        groups: {
            bro_ti: 'default',
            bt_bro_ti: 'default',
            bt_suricata_manual: 'default',
            btconf: 'default',
            custom_geoip: 'default',
            data_tags: 'default',
            eexportd_conf: 'default',
            file_hash: 'default',
            pcap_config: 'default',
            sensor_upgrade: 'default',
            suricata_config: 'default',
            suricata_rules: 'default',
            system_config: 'default',
            zeek_indicators: 'default',
        },
        data: { name: '' },
        host: '',
        password: '',
        flags: 0,
    });
    const [policySelect, setPolicySelect] = useState({
        bro_ti: 'default',
        bt_bro_ti: 'default',
        bt_suricata_manual: 'default',
        btconf: 'default',
        custom_geoip: 'default',
        data_tags: 'default',
        eexportd_conf: 'default',
        file_hash: 'default',
        pcap_config: 'default',
        sensor_upgrade: 'default',
        suricata_config: 'default',
        suricata_rules: 'default',
        system_config: 'default',
        zeek_indicators: 'default',
    });
    const [metadata, setMetadata] = useState({ available: false, enabled: false });
    const [pcap, setPCAP] = useState({ available: false, enabled: false });
    const [tcp, setTCP] = useState({ available: false, enabled: false });
    const [isMetadata, setIsMetadata] = useState(false);
    const [isPCAP, setIsPCAP] = useState(false);
    const [isTCP, setIsTCP] = useState(false);
    const [ixia, setIXIA] = useState({ enabled: false, eula: true, clkey: '' });
    const [localSNMP, setLocalSNMP] = useState({ syslocation: '', syscontact: '' });
    const [gre, setGRE] = useState({ gre_remote: '', gre_local: '', gre_key: '', gre_mtu: '' });
    const [vxlan, setVXLAN] = useState({ vxlan_id: '', vxlan_dstport: '', vxlan_mtu: '' });
    const [disableCreate, setDisableCreate] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [disableDeleteOK, setDisableDeleteOK] = useState(false);
    const sensorForm = useValidate({});
    const [original, setOriginal] = useState();
    const [ogIXIA, setOGIXIA] = useState();
    const [ogLocalSNMP, setOGLOCALSNMP] = useState();
    const [ogGRE, setOGGRE] = useState();
    const [ogVXLAN, setOGVXLAN] = useState();
    const [isUpdating, setIsUpdating] = useState(false);
    const [init, setInit] = useState(true);
    const { systemSettings, updateSystemSettings, refreshSensorNames } = useContext(DataContext);
    const sensorPerms = Auth.permissions.get('Sensors');
    const policyPerms = {
        bro_ti: Auth.permissions.get('ZeekScriptsPolicy'),
        custom_geoip: Auth.permissions.get('GeoIPPolicy'),
        eexportd_conf: Auth.permissions.get('ExportsPolicy'),
        pcap_config: Auth.permissions.get('PcapPolicy'),
        data_tags: Auth.permissions.get('DataTuningPolicy'),
        file_hash: Auth.permissions.get('FileHashPolicy'),
        system_config: Auth.permissions.get('ConfigPolicy'),
        sensor_upgrade: Auth.permissions.get('UpgradePolicy'),
        suricata_rules: Auth.permissions.get('SuricataRulesPolicy'),
        suricata_config: Auth.permissions.get('SuricataVarsPolicy'),
        zeek_indicators: Auth.permissions.get('ThreatIntelPolicy'),
    };

    useEffect(() => {
        let sensorInterval;
        if (params && params.uuid && policies.length) {
            getSensor();
            // sensorInterval = setInterval(() => {
            //     getSensor();
            // }, 10000);
        }

        return () => {
            clearInterval(sensorInterval);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [policies]);

    useEffect(() => {
        PolicyService.getPoliciesWithTypes().then((res) => {
            setPolicies(res);
        });

        // Get local SNMP once on load
        getLocalSNMP();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getLocalSNMP = () => {
        SensorsService.getLocalSNMP(params.uuid).then(res => {
            setLocalSNMP(res);
            setOGLOCALSNMP(res);
        });
    };

    const getSensor = () => {
        SensorsService.getSensor(params.uuid).then(res => {
            const ns = { ...newSensor };
            const ps = { ...policySelect };
            ns.data.name = res.data.name;
            res.deliveries ||= {};
            Object.keys(res.deliveries).forEach((v) => {
                if (v === 'sensor_upgrade') {
                    const j = _get(res, 'data.jobs', {});
                    if (Object.prototype.hasOwnProperty.call(j, 'sensor_upgrade')) {
                        ns.groups[v] = j.sensor_upgrade.name;
                        ps[v] = j.sensor_upgrade.name;
                    } else {
                        ns.groups[v] = 'default';
                        ps[v] = 'default';
                    }
                } else {
                    if (res.deliveries[v].name !== 'default' && !(res.data.jobs && Object.prototype.hasOwnProperty.call(res.data.jobs, v))) {
                        ns.groups[v] = res.deliveries[v].name;
                        ps[v] = res.deliveries[v].name;
                    } else if (res.data.jobs && Object.prototype.hasOwnProperty.call(res.data.jobs, v)) {
                        const i = policies.findIndex(x => x.type === v);
                        if (i !== -1) {
                            const j = policies[i].policies.findIndex(y => y.name === res.data.jobs[v].name);
                            if (j !== -1) {
                                ns.groups[v] = policies[i].policies[j].name;
                                ps[v] = policies[i].policies[j].name;
                            }
                        }
                    } else {
                        ns.groups[v] = res.deliveries[v].name;
                        ps[v] = res.deliveries[v].name;
                    }
                }
            });

            setNewSensor(ns);
            setUpdateSensor(res);
            setOriginal(_cloneDeep(ns));

            if (init) {
                setPolicySelect(ps);

                SensorsService.getMetadataFeature(params.uuid).then(res => {
                    setMetadata(res);
                    setIsMetadata(res.enabled);
                });
                SensorsService.getPCAPFeature(params.uuid).then(res => {
                    setPCAP(res);
                    setIsPCAP(res.enabled);
                });
                SensorsService.getTCPFeature(params.uuid).then(res => {
                    setTCP(res);
                    setIsTCP(res.enabled);
                });

                const IXIA = {
                    enabled: res.data.ixia.enabled,
                    eula: res.data.ixia.eula,
                    clkey: res.data.ixia.clkey,
                };
                const GRE = {
                    gre_remote: res.data.gre_remote,
                    gre_local: res.data.gre_local,
                    gre_key: res.data.gre_key,
                    gre_mtu: res.data.gre_mtu,
                };
                const VXLAN = {
                    vxlan_id: res.data.vxlan_id || '',
                    vxlan_dstport: res.data.vxlan_dstport || '',
                    vxlan_mtu: res.data.vxlan_mtu || '',
                };

                setIXIA(IXIA);
                setGRE(GRE);
                setVXLAN(VXLAN);
                setOGIXIA(_cloneDeep(IXIA));
                setOGGRE(_cloneDeep(GRE));
                setOGVXLAN(_cloneDeep(VXLAN));
            }
            setInit(false);
        });
    };

    const handleName = (e, validID, valid) => {
        sensorForm.set({ ...sensorForm.get(), [validID]: valid });

        const n = { ...newSensor };
        n.data.name = e.target.value;
        setNewSensor(n);
    };

    const handleText = (e, validID, valid) => {
        sensorForm.set({ ...sensorForm.get(), [validID]: valid });

        setNewSensor({
            ...newSensor,
            [e.target.name]: e.target.value,
        });
    };

    const handlePolicySelect = (e, c) => {
        if (c.props.value.includes('.mc2')) {
            toast.warning('Cannot push MC2 policy from CMC');
            return;
        }
        const ns = { ...newSensor };
        const ps = { ...policySelect };
        ns.groups[c.props.type] = c.props.value;
        ps[c.props.type] = c.props.value;
        setNewSensor(ns);
        setPolicySelect(ps);
    };

    const handleCreate = () => {
        setDisableCreate(true);
        toast.info('Adding sensor...');

        const ns = _cloneDeep(newSensor);
        ns.groups = _cloneDeep(policySelect);
        Object.keys(ns.groups).forEach(k => {
            if (ns.groups[k] === 'default') {
                delete ns.groups[k];
            }
        });

        SensorsService.addSensor(ns).then(res => {
            refreshSensorNames(true);
            const btsensor = _get(res, 'object.data.backtesting', false);
            const bt = _get(systemSettings, 'support.backtesting', {});
            if (btsensor && (!bt.exists || !bt.ui)) {
                updateSystemSettings({
                    support: {
                        backtesting: {
                            exists: btsensor,
                            ui: btsensor,
                        }
                    }
                }, 'Backtesting has been enabled');
            }
            toast.success('Successfully started sensor registration');
            setDisableCreate(false);
            navigate('/sensors');
        }, err => {
            toast.error(err);
            setDisableCreate(false);
        });
    };

    const handleUpdate = () => {
        const policyProms = [];
        const updatedSensorData = {
            data: {
                ..._cloneDeep(updateSensor.data),
            },
            update_gre: false,
            update_ixia: false,
            update_vxlan: false,
        };

        if (updateSensor.data.name !== newSensor.data.name) {
            updatedSensorData.data.name = newSensor.data.name;
        }

        if (checkIXIA()) {
            updatedSensorData.update_ixia = true;
            updatedSensorData.data.ixia = { ...ixia };
        }

        if (checkGRE()) {
            updatedSensorData.update_gre = true;
            updatedSensorData.data.gre_remote = gre.gre_remote;
            updatedSensorData.data.gre_mtu = parseInt(gre.gre_mtu) || 0;
            updatedSensorData.data.gre_local = gre.gre_local;
            updatedSensorData.data.gre_key = parseInt(gre.gre_key) || -1;
        }

        if (checkVXLAN() && (!vxlan.vxlan_dstport || !vxlan.vxlan_id)) {
            updatedSensorData.update_vxlan = true;
            delete updatedSensorData.data.vxlan_dstport;
            delete updatedSensorData.data.vxlan_mtu;
            delete updatedSensorData.data.vxlan_id;
        } else if (checkVXLAN()) {
            updatedSensorData.update_vxlan = true;
            updatedSensorData.data.vxlan_dstport = parseInt(vxlan.vxlan_dstport);
            updatedSensorData.data.vxlan_id = parseInt(vxlan.vxlan_id);
            updatedSensorData.data.vxlan_mtu = parseInt(vxlan.vxlan_mtu) || 1450;
        }

        const ps = _cloneDeep(newSensor);
        const ns = _cloneDeep(newSensor);
        const os = _cloneDeep(original);

        delete ns.groups;
        delete os.groups;

        if (!_isEqual(ns, os) || !_isEqual(ixia, ogIXIA) || !_isEqual(gre, ogGRE) || !_isEqual(vxlan, ogVXLAN)) {
            policyProms.push(SensorsService.updateSensor(params.uuid, updatedSensorData));
        }

        ps.groups = policySelect;

        const delvs = Object.keys(updateSensor.deliveries);
        delvs.forEach((v) => {
            if (v !== 'api' && v !== 'sensor_upgrades' && v !== 'suricata_iprep') {
                if (
                    updateSensor.deliveries[v].name !== _get(updateSensor, `data.jobs[${v}].name`, ps.groups[v]) ||
                    updateSensor.deliveries[v].name !== ps.groups[v]) {
                    policyProms.push(PolicyService.assignPolicy(v, ps.groups[v], [params.uuid]));
                }
            }
        });

        // Manually check for sensor_upgrade
        const j = _get(updateSensor, 'data.jobs', {});
        if (Object.prototype.hasOwnProperty.call(j, 'sensor_upgrade')) {
            if (ps.groups.sensor_upgrade !== j.sensor_upgrade.name) {
                policyProms.push(PolicyService.assignPolicy('sensor_upgrade', ps.groups.sensor_upgrade, [params.uuid]));
            }
        }

        setIsUpdating(true);

        if (metadata.enabled !== isMetadata) {
            handleMetadataUpdate();
        }

        if (pcap.enabled !== isPCAP) {
            handlePCAPUpdate();
        }

        if (tcp.enabled !== isTCP) {
            handleTCPUpdate();
        }

        if (!_isEqual(localSNMP, ogLocalSNMP)) {
            handleLocalSNMPUpdate();
        }

        Promise.all(policyProms).then((r) => {
            // navigate('/sensors');
            setTimeout(() => {
                setIsUpdating(false);
                toast.success('Sensor updated successfully');
                getSensor();
            }, 2000);
        }, err => {
            console.log(err);
            setTimeout(() => {
                setIsUpdating(false);
                if (err && typeof err === 'string') {
                    toast.error(err);
                }
            }, 2000);
        });
    };

    const handleCancel = () => {
        navigate('/sensors');
    };

    const handleMetadataEnable = (e) => {
        setIsMetadata(e.currentTarget.checked);
    };

    const handlePCAPEnable = (e) => {
        setIsPCAP(e.currentTarget.checked);
    };

    const handleTCPEnable = (e) => {
        setIsTCP(e.currentTarget.checked);
    };

    const handleMetadataUpdate = () => {
        SensorsService.updateMetadataFeature(params.uuid, {
            feature: {
                enable_please: !metadata.enabled,
            }
        }).then(res => {
            setMetadata(res);
        });
    };

    const handlePCAPUpdate = () => {
        SensorsService.updatePCAPFeature(params.uuid, {
            feature: {
                enable_please: !pcap.enabled,
            }
        }).then(res => {
            setPCAP(res);
        });
    };

    const handleTCPUpdate = () => {
        SensorsService.updateTCPFeature(params.uuid, {
            feature: {
                enable_please: !tcp.enabled,
            }
        }).then(res => {
            setTCP(res);
        });
    };

    const handleLocalSNMPUpdate = () => {
        SensorsService.updateLocalSNMP(params.uuid, localSNMP).then(res => {
            setLocalSNMP(res);
        });
    };

    const isIXIAValid = () => {
        if (ixia.enabled) {
            if (ixia.eula && ixia.clkey) {
                return true;
            }
            return false;
        }
        return true;
    };

    const checkIXIA = () => {
        if (updateSensor) {
            for (const x in ixia) {
                if (Object.prototype.hasOwnProperty.call(ixia, x)) {
                    if (ixia[x] !== updateSensor.data.ixia[x]) {
                        return true;
                    }
                }
            }
        }

        return false;
    };

    const checkGRE = () => {
        if (updateSensor) {
            for (const x in gre) {
                if (Object.prototype.hasOwnProperty.call(gre, x)) {
                    if (gre[x] !== updateSensor.data[x]) {
                        return true;
                    }
                }
            }
        }

        return false;
    };

    const checkVXLAN = () => {
        if (updateSensor) {
            for (const x in vxlan) {
                if (Object.prototype.hasOwnProperty.call(vxlan, x)) {
                    if (!vxlan[x] && !updateSensor.data[x]) {
                        return false;
                    } else if (vxlan[x] && updateSensor.data && vxlan[x] === updateSensor.data) {
                        return false;
                    } else if (!vxlan[x] && updateSensor.data[x]) {
                        return true;
                    } else if (vxlan[x] && !updateSensor.data[x]) {
                        return true; //
                    } else if (vxlan[x] && updateSensor.data && vxlan[x] !== updateSensor.data) {
                        return true;
                    }
                }
            }
        }

        return false;
    };

    const handleUpdateIXIA = (e) => {
        setIXIA({
            ...ixia,
            [e.currentTarget.name]: e.currentTarget.type === 'checkbox' ? e.currentTarget.checked : e.currentTarget.value.trim(),
        });
    };

    const handleUpdateLocalSNMP = (e) => {
        setLocalSNMP({
            ...localSNMP,
            [e.target.name]: e.target.value,
        });
    };

    const handleUpdateGRE = (e) => {
        setGRE({
            ...gre,
            [e.currentTarget.name]: e.currentTarget.value,
        });
    };

    const handleUpdateVXLAN = (e) => {
        setVXLAN({
            ...vxlan,
            [e.currentTarget.name]: e.currentTarget.value,
        });
    };

    // No op placeholders
    const handleIXIADelete = () => {
        setIXIA({
            enabled: false,
            eula: false,
            clkey: '',
        });
    };

    const handleGREDelete = () => {
        setGRE({
            gre_remote: '',
            gre_local: '',
            gre_key: '',
            gre_mtu: '',
        });
    };

    const handleVXLANDelete = () => {
        setVXLAN({
            vxlan_dstport: '',
            vxlan_id: '',
            vxlan_mtu: '',
        });
    };

    const handleOpenDelete = () => {
        setDeleteOpen(!deleteOpen);
    };

    const handleDeleteSensor = () => {
        setDisableDeleteOK(true);
        SensorsService.deleteSensor(updateSensor.uuid).then(() => {
            toast.success('Sensor deleted successfully');
            navigate(-1);
            setDisableDeleteOK(true);
        }, err => {
            toast.error(err);
            setDeleteOpen(false);
            setDisableDeleteOK(true);
        });
    };

    const handleUpdateForceRegistration = (e) => {
        setNewSensor({
            ...newSensor,
            flags: e.target.checked ? 1 : 0,
        });
    };

    return (
        <Paper elevation={3}>
            <Box p={5}>
                <Grid container spacing={4}>
                    <Grid item xs={10}>
                        <RadioGroup row>
                            <Box display={metadata.available ? 'block' : 'none'}>
                                {sensorPerms.admin && <FormControlLabel
                                    label="Metadata Collection"
                                    control={
                                        <Checkbox
                                            checked={isMetadata}
                                            onChange={handleMetadataEnable}
                                        />
                                    }
                                />}
                            </Box>
                            <Box display={pcap.available ? 'block' : 'none'}>
                                {sensorPerms.admin && <FormControlLabel
                                    label="Packet Capture"
                                    control={
                                        <Checkbox
                                            checked={isPCAP}
                                            onChange={handlePCAPEnable}
                                        />
                                    }
                                />}
                            </Box>
                            <Box display={tcp.available ? 'block' : 'none'}>
                                {sensorPerms.admin && <FormControlLabel
                                    label="Advanced TCP Processing"
                                    control={
                                        <Checkbox
                                            checked={isTCP}
                                            onChange={handleTCPEnable}
                                        />
                                    }
                                />}
                            </Box>
                        </RadioGroup>
                    </Grid>
                    <Grid item xs={2}>
                        <Box display={updateSensor ? 'block' : 'none'}>
                            {sensorPerms.delete && <Button
                                className={classes.buttonRight}
                                variant="contained"
                                aria-label="delete"
                                color="secondary"
                                onClick={handleOpenDelete}
                            >
                                <DeleteIcon />
                                Delete
                            </Button>}
                        </Box>
                    </Grid>
                </Grid>
                <Grid container spacing={5}>
                    <Grid container item xs={4}>
                        <Grid item xs={12}>
                            <Box hidden={!!updateSensor}>
                                <Validator
                                    valueType="host"
                                    errorText="Hostname or IP is invalid"
                                    fullWidth
                                    label="Host"
                                    name="host"
                                    value={newSensor.host}
                                    onChange={handleText}
                                />
                            </Box>
                            <Box mt={updateSensor ? 0 : 3}>
                                <Validator
                                    valueType="sensorName"
                                    errorText="Sensor name is invalid"
                                    fullWidth
                                    label="Name"
                                    name="name"
                                    value={newSensor.data.name}
                                    onChange={handleName}
                                />
                            </Box>
                            <Box mt={3} hidden={!!updateSensor}>
                                <Validator
                                    allowEmpty={!!updateSensor}
                                    errorText="Password cannot be empty"
                                    type="password"
                                    fullWidth
                                    label="Password"
                                    name="password"
                                    value={newSensor.password}
                                    onChange={handleText}
                                />
                            </Box>
                            <Box mt={3} hidden={!updateSensor}>
                                <TextField
                                    fullWidth
                                    label="SNMP sysLocation (policy override)"
                                    name="syslocation"
                                    value={localSNMP.syslocation}
                                    onChange={handleUpdateLocalSNMP}
                                />
                            </Box>
                            <Box mt={3} hidden={!updateSensor}>
                                <TextField
                                    fullWidth
                                    label="SNMP sysContact (policy override)"
                                    name="syscontact"
                                    value={localSNMP.syscontact}
                                    onChange={handleUpdateLocalSNMP}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container item xs={8} spacing={5}>
                        {policies.map((v, i) => {
                            if (v.type === 'btconf') return null;
                            let color = theme.palette.text.primary;
                            const j = v.policies.findIndex(z => z.name === newSensor.groups[v.type]);
                            if (j >= 0) {
                                color = v.policies[j].color;
                            }
                            if (updateSensor && updateSensor.data.backtesting && BacktestingService.policies[v.type]) {
                                return null;
                            } else {
                                return (
                                    <Grid key={v.type + '-' + i} item xs={4}>
                                        <Box>
                                            <InputLabel id={v.type + '-label'}>{POLICY_NAMES[v.type]}</InputLabel>
                                            <Select
                                                variant="standard"
                                                fullWidth
                                                labelId={v.type + '-label'}
                                                value={policySelect[v.type] || 'default'}
                                                onChange={handlePolicySelect}
                                                style={{ color: color }}
                                                disabled={(!sensorPerms.override && policySelect[v.type].includes('.mc2')) || !policyPerms[v.type].admin}
                                            >
                                                {v.policies.map((x, j) => {
                                                    return (
                                                        <MenuItem
                                                            key={x.name + '-policy-' + j}
                                                            type={v.type}
                                                            value={x.name}
                                                            style={{ color: x.color }}
                                                        >
                                                            {x.name}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </Box>
                                    </Grid>
                                );
                            }
                        })}
                    </Grid>
                    <Box p={3} className={classes.fullWidth} hidden={!updateSensor || updateSensor.data.backtesting}>
                        <Grid container spacing={4}>
                            {sensorPerms.admin && <Grid item xs={4}>
                                <Paper elevation={3} className={classes.sensorConfigs}>
                                    <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                                        <Box display="flex" width="100%" flexDirection="column">
                                            <Typography variant="h6">IXIA Config</Typography>
                                            <Box>
                                                <FormControlLabel
                                                    label="Enable CloudLens"
                                                    control={
                                                        <Checkbox
                                                            name="enabled"
                                                            checked={ixia.enabled}
                                                            onChange={handleUpdateIXIA}
                                                        />
                                                    }
                                                />
                                            </Box>
                                            <Box mt={3}>
                                                <FormControlLabel
                                                    label="Please confirm acceptance of CloudLens EULA (required)"
                                                    control={
                                                        <Checkbox
                                                            name="eula"
                                                            checked={ixia.eula}
                                                            onChange={handleUpdateIXIA}
                                                        />
                                                    }
                                                    disabled={!ixia.enabled}
                                                />
                                            </Box>
                                            <Box mt={3}>
                                                <TextField
                                                    label="Project Key"
                                                    fullWidth
                                                    name="clkey"
                                                    value={ixia.clkey}
                                                    onChange={handleUpdateIXIA}
                                                    disabled={!ixia.enabled}
                                                />
                                            </Box>
                                        </Box>
                                        <Box mt={3} className={classes.fullWidth} display="flex" justifyContent="flex-end">
                                            <Button
                                                className={classes.buttonRight}
                                                variant="contained"
                                                color="secondary"
                                                aria-label="delete"
                                                onClick={handleIXIADelete}
                                            >
                                                <DeleteIcon />
                                                Delete
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>}
                            {sensorPerms.admin && <Grid item xs={4}>
                                <Paper elevation={3} className={classes.sensorConfigs}>
                                    <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                                        <Box display="flex" width="100%" flexDirection="column">
                                            <Typography variant="h6">GRE Config</Typography>
                                            <Box>
                                                <TextField
                                                    fullWidth
                                                    label="Remote IP"
                                                    placeholder="Remote GRE IP Address"
                                                    name="gre_remote"
                                                    value={gre.gre_remote}
                                                    onChange={handleUpdateGRE}
                                                />
                                            </Box>
                                            <Box mt={3}>
                                                <TextField
                                                    fullWidth
                                                    label="Local IP"
                                                    placeholder="Any"
                                                    name="gre_local"
                                                    value={gre.gre_local}
                                                    onChange={handleUpdateGRE}
                                                />
                                            </Box>
                                            <Box mt={3}>
                                                <TextField
                                                    fullWidth
                                                    label="Key"
                                                    placeholder="GRE Header key (-1 for default)"
                                                    name="gre_key"
                                                    value={gre.gre_key}
                                                    onChange={handleUpdateGRE}
                                                />
                                            </Box>
                                            <Box mt={3}>
                                                <TextField
                                                    fullWidth
                                                    label="MTU"
                                                    placeholder="1454"
                                                    type="number"
                                                    name="gre_mtu"
                                                    value={gre.gre_mtu}
                                                    onChange={handleUpdateGRE}
                                                />
                                            </Box>
                                        </Box>
                                        <Box mt={3} className={classes.fullWidth} display="flex" justifyContent="flex-end">
                                            <Button
                                                className={classes.buttonRight}
                                                variant="contained"
                                                color="secondary"
                                                aria-label="delete"
                                                onClick={handleGREDelete}
                                            >
                                                <DeleteIcon />
                                                Delete
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>}
                            {sensorPerms.admin && <Grid item xs={4}>
                                <Paper elevation={3} className={classes.sensorConfigs}>
                                    <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                                        <Box display="flex" width="100%" flexDirection="column">
                                            <Typography variant="h6">VXLAN Config</Typography>
                                            <Box>
                                                <TextField
                                                    fullWidth
                                                    label="ID"
                                                    type="number"
                                                    name="vxlan_id"
                                                    value={vxlan.vxlan_id}
                                                    onChange={handleUpdateVXLAN}
                                                />
                                            </Box>
                                            <Box mt={3}>
                                                <TextField
                                                    fullWidth
                                                    label="Destination Port"
                                                    type="number"
                                                    name="vxlan_dstport"
                                                    value={vxlan.vxlan_dstport}
                                                    onChange={handleUpdateVXLAN}
                                                />
                                            </Box>
                                            <Box mt={3}>
                                                <TextField
                                                    fullWidth
                                                    label="MTU"
                                                    type="number"
                                                    name="vxlan_mtu"
                                                    value={vxlan.vxlan_mtu}
                                                    onChange={handleUpdateVXLAN}
                                                />
                                            </Box>
                                        </Box>
                                        <Box mt={3} className={classes.fullWidth} display="flex" justifyContent="flex-end">
                                            <Button
                                                className={classes.buttonRight}
                                                variant="contained"
                                                color="secondary"
                                                aria-label="delete"
                                                onClick={handleVXLANDelete}
                                            >
                                                <DeleteIcon />
                                                Delete
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>}
                        </Grid>
                    </Box>
                    <Grid item xs={12} container justifyContent="flex-end">
                        <Box ml={2} display={updateSensor ? 'none' : 'block'}>
                            <FormControlLabel
                                label="Force Registration"
                                labelPlacement="start"
                                control={
                                    <Checkbox
                                        checked={Boolean(newSensor.flags)}
                                        onChange={handleUpdateForceRegistration}
                                    />
                                }
                            />
                        </Box>
                        <Box ml={2}><Button variant="contained" aria-label="cancel" onClick={handleCancel}>Back</Button></Box>
                        <Box ml={2}>{(() => {
                            if (updateSensor) {
                                return (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        aria-label="update"
                                        onClick={handleUpdate}
                                        disabled={!isIXIAValid() || !sensorPerms.write}
                                    >Update</Button>
                                );
                            }
                            return (
                                <Button
                                    className={classes.buttonRight}
                                    variant="contained"
                                    color="primary"
                                    aria-label="create"
                                    onClick={handleCreate}
                                    disabled={!checkValidity(sensorForm.get()) || disableCreate || !sensorPerms.write}
                                >
                                    Create
                                </Button>
                            );
                        })()}</Box>
                    </Grid>
                </Grid>
                <Backdrop className={classes.backdrop} open={isUpdating}>
                    <Paper elevation={3}>
                        <Box><LinearProgress /></Box>
                        <Box p={4}>
                            <Box>
                                <Typography>Please wait for the sensor to finish updating...</Typography>
                                <Box mt={2} display={original && !_isEqual(original.groups, newSensor.groups) ? 'block' : 'none'}>
                                    <Typography>Pushing policies...</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Backdrop>
                <ConfirmationDialog
                    onClose={handleOpenDelete}
                    onOK={handleDeleteSensor}
                    open={deleteOpen}
                    message={(() => {
                        return `Are you sure you want to delete the sensor ${updateSensor && (updateSensor.data.name || updateSensor.host)}?`;
                    })()}
                    title="Delete Sensor"
                    cancelLabel="Cancel"
                    okLabel="Yes"
                    disabled={disableDeleteOK}
                />
            </Box>
        </Paper>
    );
};

export default AddSensor;
